// src/admin/AttendanceDateManagement.js
import React, { useState, useEffect, useContext, useMemo } from 'react';
import { StudentContext } from '../StudentContext';
import { firestore } from '../firebaseConfig';
import { doc, getDocs, collection, setDoc, onSnapshot, Timestamp } from 'firebase/firestore';
import './AttendanceDateManagement.css';

const AttendanceDateManagement = () => {
  const { currentYearId } = useContext(StudentContext);

  const [dateLocks, setDateLocks] = useState({});
  const [registeredDates, setRegisteredDates] = useState([]);
  const [discoveredDates, setDiscoveredDates] = useState([]);
  const [newDateInput, setNewDateInput] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all'); // 'all' | 'locked' | 'unlocked'
  const [isProcessing, setIsProcessing] = useState(false);
  const [message, setMessage] = useState({ text: '', type: '' });

  // 1. Lắng nghe trạng thái khóa ngày (Realtime)
  useEffect(() => {
    if (!currentYearId) return;

    const locksRef = doc(firestore, `academic_years/${currentYearId}/settings`, 'attendance_locks');
    const unsubLocks = onSnapshot(locksRef, (snap) => {
      if (snap.exists()) {
        setDateLocks(snap.data() || {});
      } else {
        setDateLocks({});
      }
    });

    // Lắng nghe danh sách ngày do Admin chủ động đăng ký
    const datesRef = doc(firestore, `academic_years/${currentYearId}/settings`, 'attendance_dates');
    const unsubDates = onSnapshot(datesRef, (snap) => {
      if (snap.exists() && Array.isArray(snap.data()?.dates)) {
        setRegisteredDates(snap.data().dates);
      } else {
        setRegisteredDates([]);
      }
    });

    return () => {
      unsubLocks();
      unsubDates();
    };
  }, [currentYearId]);

  // 2. Tự động quét các ngày đã từng có học sinh điểm danh trong student_attendances
  useEffect(() => {
    const discoverExistingDates = async () => {
      if (!currentYearId) return;
      try {
        const snap = await getDocs(collection(firestore, `academic_years/${currentYearId}/student_attendances`));
        const foundSet = new Set();

        snap.docs.forEach((d) => {
          const data = d.data();
          if (data.church) {
            Object.keys(data.church).forEach((date) => foundSet.add(date));
          }
          if (data.catechism) {
            Object.keys(data.catechism).forEach((date) => foundSet.add(date));
          }
        });

        setDiscoveredDates(Array.from(foundSet));
      } catch (err) {
        console.error('Lỗi quét ngày điểm danh thực tế:', err);
      }
    };

    discoverExistingDates();
  }, [currentYearId]);

  // 3. Gộp tất cả các ngày từ: Đã đăng ký + Quét được + Đã có trong DateLocks
  const allDates = useMemo(() => {
    const dateSet = new Set([
      ...registeredDates,
      ...discoveredDates,
      ...Object.keys(dateLocks),
    ]);

    return Array.from(dateSet)
      .filter((d) => /^\d{4}-\d{2}-\d{2}$/.test(d))
      .sort((a, b) => b.localeCompare(a)); // Mới nhất lên đầu
  }, [registeredDates, discoveredDates, dateLocks]);

  // Lọc theo tìm kiếm & trạng thái
  const filteredDates = useMemo(() => {
    return allDates.filter((dateStr) => {
      const isLocked = Boolean(dateLocks[dateStr]);
      if (statusFilter === 'locked' && !isLocked) return false;
      if (statusFilter === 'unlocked' && isLocked) return false;
      if (searchTerm && !dateStr.includes(searchTerm)) return false;
      return true;
    });
  }, [allDates, dateLocks, statusFilter, searchTerm]);

  // Thông báo tạm thời
  const showToast = (text, type = 'success') => {
    setMessage({ text, type });
    setTimeout(() => setMessage({ text: '', type: '' }), 4000);
  };

  // Thêm ngày điểm danh mới
  const handleAddNewDate = async (e) => {
    e.preventDefault();
    if (!newDateInput) return;
    if (!currentYearId) {
      showToast('Chưa xác định được niên khóa hiện tại!', 'error');
      return;
    }

    try {
      setIsProcessing(true);
      const updatedDates = Array.from(new Set([...registeredDates, newDateInput])).sort();

      const datesRef = doc(firestore, `academic_years/${currentYearId}/settings`, 'attendance_dates');
      await setDoc(datesRef, {
        dates: updatedDates,
        updatedAt: Timestamp.now()
      }, { merge: true });

      setNewDateInput('');
      showToast(`Đã thêm ngày ${newDateInput} vào danh sách điểm danh!`, 'success');
    } catch (err) {
      showToast('Lỗi khi thêm ngày: ' + err.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Bật / Tắt khóa 1 ngày
  const handleToggleLock = async (dateStr) => {
    if (!currentYearId) return;
    const currentStatus = Boolean(dateLocks[dateStr]);
    const nextStatus = !currentStatus;

    try {
      const locksRef = doc(firestore, `academic_years/${currentYearId}/settings`, 'attendance_locks');
      await setDoc(locksRef, {
        [dateStr]: nextStatus
      }, { merge: true });

      // Đồng bộ settings toàn cục
      await setDoc(doc(firestore, 'settings', 'attendance_locks'), {
        [dateStr]: nextStatus
      }, { merge: true });

      showToast(`Đã ${nextStatus ? '🔒 khóa' : '🔓 mở khóa'} ngày ${dateStr}!`);
    } catch (err) {
      showToast('Lỗi khi cập nhật trạng thái: ' + err.message, 'error');
    }
  };

  // Khóa tất cả các ngày trong quá khứ (trước ngày hôm nay)
  const handleLockPastDates = async () => {
    if (!currentYearId || allDates.length === 0) return;
    const todayStr = new Date().toLocaleDateString('sv-SE');

    if (!window.confirm(`Bạn có chắc muốn KHÓA tất cả các ngày trước hôm nay (${todayStr})?`)) {
      return;
    }

    try {
      setIsProcessing(true);
      const updates = {};
      let count = 0;

      allDates.forEach((d) => {
        if (d < todayStr && !dateLocks[d]) {
          updates[d] = true;
          count++;
        }
      });

      if (count === 0) {
        showToast('Tất cả các ngày trong quá khứ đã được khóa trước đó.', 'info');
        setIsProcessing(false);
        return;
      }

      const locksRef = doc(firestore, `academic_years/${currentYearId}/settings`, 'attendance_locks');
      await setDoc(locksRef, updates, { merge: true });

      await setDoc(doc(firestore, 'settings', 'attendance_locks'), updates, { merge: true });

      showToast(`Đã khóa thành công ${count} ngày điểm danh trong quá khứ!`);
    } catch (err) {
      showToast('Lỗi khi khóa các ngày cũ: ' + err.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  // Mở khóa tất cả các ngày
  const handleUnlockAll = async () => {
    if (!currentYearId || allDates.length === 0) return;

    if (!window.confirm('Bạn có chắc muốn MỞ KHÓA TẤT CẢ các ngày? GLV sẽ có thể điểm danh bổ sung.')) {
      return;
    }

    try {
      setIsProcessing(true);
      const updates = {};
      allDates.forEach((d) => {
        updates[d] = false;
      });

      const locksRef = doc(firestore, `academic_years/${currentYearId}/settings`, 'attendance_locks');
      await setDoc(locksRef, updates, { merge: true });

      await setDoc(doc(firestore, 'settings', 'attendance_locks'), updates, { merge: true });

      showToast('Đã mở khóa toàn bộ các ngày điểm danh!');
    } catch (err) {
      showToast('Lỗi khi mở khóa: ' + err.message, 'error');
    } finally {
      setIsProcessing(false);
    }
  };

  const formatDisplayDate = (dateStr) => {
    if (!dateStr) return '';
    const parts = dateStr.split('-');
    if (parts.length !== 3) return dateStr;
    const dateObj = new Date(Number(parts[0]), Number(parts[1]) - 1, Number(parts[2]));
    const days = ['Chúa Nhật', 'Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy'];
    return `${days[dateObj.getDay()]} - Ngày ${parts[2]}/${parts[1]}/${parts[0]}`;
  };

  // Thống kê nhanh
  const totalCount = allDates.length;
  const lockedCount = allDates.filter((d) => dateLocks[d]).length;
  const unlockedCount = totalCount - lockedCount;

  return (
    <div className="adm-date-mgmt">
      <div className="adm-header">
        <div>
          <h2>📅 Quản Lý Ngày Điểm Danh & Khóa Điểm Danh</h2>
          <p className="adm-subtitle">
            Khóa ngày điểm danh sẽ <strong>ẩn lựa chọn "Có điểm danh"</strong> trong 
            <em> AttendanceSundayTab</em> và <em>AttendanceCatechismTab</em>, ngăn Giáo lý viên tự ý bổ sung điểm danh sau thời hạn.
          </p>
        </div>
      </div>

      {message.text && (
        <div className={`adm-alert ${message.type}`}>
          {message.text}
        </div>
      )}

      {/* Thẻ Thống kê */}
      <div className="adm-stat-cards">
        <div className="adm-stat-card">
          <span className="stat-num">{totalCount}</span>
          <span className="stat-label">Tổng số ngày điểm danh</span>
        </div>
        <div className="adm-stat-card stat-unlocked">
          <span className="stat-num">{unlockedCount}</span>
          <span className="stat-label">🟢 Đang mở (Được bổ sung)</span>
        </div>
        <div className="adm-stat-card stat-locked">
          <span className="stat-num">{lockedCount}</span>
          <span className="stat-label">🔒 Đã khóa (Không sửa được)</span>
        </div>
      </div>

      {/* Thanh Thao tác Nhanh & Thêm Ngày */}
      <div className="adm-toolbar">
        <form onSubmit={handleAddNewDate} className="adm-add-date-form">
          <input
            type="date"
            value={newDateInput}
            onChange={(e) => setNewDateInput(e.target.value)}
            className="adm-input-date"
            required
          />
          <button type="submit" className="adm-btn adm-btn-add" disabled={isProcessing}>
            ➕ Thêm ngày điểm danh
          </button>
        </form>

        <div className="adm-actions-group">
          <button
            type="button"
            className="adm-btn adm-btn-lock-past"
            onClick={handleLockPastDates}
            disabled={isProcessing}
            title="Tự động khóa toàn bộ các ngày trước ngày hôm nay"
          >
            🔒 Khóa các ngày đã qua
          </button>
          <button
            type="button"
            className="adm-btn adm-btn-unlock-all"
            onClick={handleUnlockAll}
            disabled={isProcessing}
          >
            🔓 Mở khóa tất cả
          </button>
        </div>
      </div>

      {/* Bộ Lọc & Tìm kiếm */}
      <div className="adm-filter-bar">
        <input
          type="text"
          placeholder="🔍 Tìm kiếm theo YYYY-MM hoặc YYYY-MM-DD..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="adm-search-input"
        />

        <div className="adm-filter-buttons">
          <button
            type="button"
            className={`adm-filter-btn ${statusFilter === 'all' ? 'active' : ''}`}
            onClick={() => setStatusFilter('all')}
          >
            Tất cả ({totalCount})
          </button>
          <button
            type="button"
            className={`adm-filter-btn ${statusFilter === 'unlocked' ? 'active' : ''}`}
            onClick={() => setStatusFilter('unlocked')}
          >
            Đang mở ({unlockedCount})
          </button>
          <button
            type="button"
            className={`adm-filter-btn ${statusFilter === 'locked' ? 'active' : ''}`}
            onClick={() => setStatusFilter('locked')}
          >
            Đã khóa ({lockedCount})
          </button>
        </div>
      </div>

      {/* Bảng Danh sách Ngày */}
      <div className="adm-table-container">
        <table className="adm-table">
          <thead>
            <tr>
              <th style={{ width: '60px', textAlign: 'center' }}>STT</th>
              <th>Ngày Điểm Danh</th>
              <th style={{ width: '130px', textAlign: 'center' }}>Mã Ngày</th>
              <th style={{ width: '160px', textAlign: 'center' }}>Trạng Thái</th>
              <th style={{ width: '180px', textAlign: 'center' }}>Thao Tác</th>
            </tr>
          </thead>
          <tbody>
            {filteredDates.length > 0 ? (
              filteredDates.map((dateStr, idx) => {
                const isLocked = Boolean(dateLocks[dateStr]);
                return (
                  <tr key={dateStr} className={isLocked ? 'row-locked' : 'row-unlocked'}>
                    <td style={{ textAlign: 'center' }}>{idx + 1}</td>
                    <td style={{ fontWeight: 600 }}>
                      {formatDisplayDate(dateStr)}
                    </td>
                    <td style={{ textAlign: 'center', fontFamily: 'monospace', fontWeight: 700 }}>
                      {dateStr}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      {isLocked ? (
                        <span className="adm-badge badge-locked">
                          🔒 ĐÃ KHÓA
                        </span>
                      ) : (
                        <span className="adm-badge badge-unlocked">
                          🟢 ĐANG MỞ
                        </span>
                      )}
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <button
                        type="button"
                        className={`adm-btn-toggle ${isLocked ? 'btn-unlock' : 'btn-lock'}`}
                        onClick={() => handleToggleLock(dateStr)}
                      >
                        {isLocked ? '🔓 Mở khóa ngày' : '🔒 Khóa ngày lại'}
                      </button>
                    </td>
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan="5" className="adm-no-data">
                  Không tìm thấy ngày điểm danh nào phù hợp.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default AttendanceDateManagement;